import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { useWorkoutTemplateList } from '../../template/useWorkoutTemplateList';

export default function WorkoutTemplatePickerModal({
  isOpen,
  weekdayName,
  onSelect,
  onClose,
}) {
  const { allTemplates, loading, setSearchTerm } = useWorkoutTemplateList();
  const [localSearch, setLocalSearch] = useState('');
  const [pendingTemplateId, setPendingTemplateId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLocalSearch('');
      setSearchTerm('');
      setPendingTemplateId(null);
    }
  }, [isOpen, setSearchTerm]);

  const filtered = useMemo(() => {
    const visible = allTemplates.filter((t) => (t.template_id ?? t.id) !== 0);
    const q = localSearch.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((t) =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  }, [allTemplates, localSearch]);

  const handlePick = async (template) => {
    if (pendingTemplateId) return;
    setPendingTemplateId(template.id);
    try {
      await onSelect({ templateId: template.id, templateName: template.title });
      onClose();
    } catch {
      // Parent shows error toast; keep modal open for retry.
    } finally {
      setPendingTemplateId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Choose template</h2>
            {weekdayName && (
              <p className="text-sm text-gray-500 capitalize">For {weekdayName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={Boolean(pendingTemplateId)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates…"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none"
              autoFocus
              disabled={Boolean(pendingTemplateId)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading templates…
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center py-8 text-gray-500 text-sm">
              {localSearch.trim() ? 'No templates match your search.' : 'No templates found.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((t) => {
                const isPending = pendingTemplateId === t.id;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => handlePick(t)}
                      disabled={Boolean(pendingTemplateId)}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-colors disabled:opacity-60"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900 block truncate">
                            {t.title}
                          </span>
                          {t.description?.trim() && (
                            <span className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                              {t.description}
                            </span>
                          )}
                        </div>
                        {isPending && (
                          <Loader2 className="w-4 h-4 text-orange-500 animate-spin shrink-0" />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={Boolean(pendingTemplateId)}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
